import type { MatchDto } from '../api';
import { batchLabel, statusLabel } from '../lib/format';

/** 比赛状态徽标（完场 / 进行中 / 未开赛）。 */
export function StatusBadge({ match }: { match: MatchDto }) {
  const live = match.status === 'live';
  return <span className={'status-badge ' + (live ? 'live' : match.status)}>{live && <i />}{statusLabel(match)}</span>;
}

interface TopBarProps {
  syncText: string;
  syncing: boolean;
  role: 'admin' | 'user' | '';
  refreshing: boolean;
  onRefresh: () => void;
  onOpenDashboard: () => void;
  onOpenAdmin: () => void;
  onLogout: () => void;
}

/** 顶部吸顶栏：站名 + 同步状态 + 刷新 + 更多（管理入口仅 admin 可见）。 */
export function TopBar({ syncText, syncing, role, refreshing, onRefresh, onOpenDashboard, onOpenAdmin, onLogout }: TopBarProps) {
  return <header className="topbar">
    <div className="topbar-brand">
      <span className="brand-mark" aria-hidden="true">◒</span>
      <div>
        <h1>核心联赛数据台</h1>
        <p className={'topbar-sync' + (syncing ? ' busy' : '')}><span className="status-dot" aria-hidden="true" />{syncText}</p>
      </div>
    </div>
    <div className="topbar-actions">
      <button type="button" className="icon-button" onClick={onRefresh} disabled={refreshing} aria-label="刷新数据">{refreshing ? '刷新中' : '刷新'}</button>
      {role === 'admin' && <button type="button" className="icon-button" onClick={onOpenDashboard}>看板</button>}
      {role === 'admin' && <button type="button" className="icon-button" onClick={onOpenAdmin}>设置</button>}
      <button type="button" className="icon-button" onClick={onLogout}>退出</button>
    </div>
  </header>;
}

interface DateBarProps {
  date: string;
  businessDay: string;
  count: number;
  onShift: (offset: number) => void;
  onPick: (value: string) => void;
  onToday: () => void;
}

/** 日期行：前一天 / 日期选择 / 后一天 / 今天（竞彩营业日）。 */
export function DateBar({ date, businessDay, count, onShift, onPick, onToday }: DateBarProps) {
  const isBusinessDay = date === businessDay;
  return <div className="datebar">
    <button type="button" className="datebar-step" onClick={() => onShift(-1)} aria-label="前一天">前一天</button>
    <label className="datebar-picker">
      <input type="date" value={date} onChange={(event) => onPick(event.target.value)} aria-label="选择日期" />
    </label>
    <button type="button" className="datebar-step" onClick={() => onShift(1)} aria-label="后一天">后一天</button>
    <button type="button" className={'datebar-today' + (isBusinessDay ? ' active' : '')} onClick={onToday}>今天</button>
    <span className="datebar-meta"><b>{count}</b> 场 · {batchLabel(date)}</span>
  </div>;
}

interface LeagueBarProps {
  value: string;
  leagues: ReadonlyArray<{ code: string; name: string }>;
  filterCount: number;
  onChange: (code: string) => void;
  onOpenFilters: () => void;
  onRunM1: () => void;
  m1Busy: boolean;
}

/** 联赛行：横向滚动 chip（全部 + 12 联赛）+ 筛选入口。 */
export function LeagueBar({ value, leagues, filterCount, onChange, onOpenFilters, onRunM1, m1Busy }: LeagueBarProps) {
  return <div className="leaguebar">
    <div className="leaguebar-scroll" role="tablist" aria-label="联赛筛选">
      <button type="button" role="tab" aria-selected={value === 'all'} className={'chip' + (value === 'all' ? ' active' : '')} onClick={() => onChange('all')}>全部</button>
      {leagues.map((league) => <button
        key={league.code}
        type="button"
        role="tab"
        aria-selected={value === league.code}
        className={'chip' + (value === league.code ? ' active' : '')}
        onClick={() => onChange(league.code)}
      >{league.name}</button>)}
    </div>
    <button type="button" className="filter-button m1-button" onClick={onRunM1} disabled={m1Busy}>{m1Busy ? '计算中' : 'M1 6串1'}</button>
    <button type="button" className="filter-button" onClick={onOpenFilters}>
      筛选{filterCount > 0 && <span className="filter-badge">{filterCount}</span>}
    </button>
  </div>;
}
